import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

import { PublicationVersionRead, getRanaApiV1 } from "@nens/rana/api-v1";

import { queryDirectly } from "./hooks/query-directly";

export function getPublicationVersionQuery(
  publicationId: string | undefined,
  publishedVersion: number | undefined,
  version: "latest" | "published" | number
) {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["get-publication-version", publicationId, version],
    queryFn: async (): Promise<PublicationVersionRead> => {
      if (publicationId === undefined) {
        throw new Error("Can't happen.");
      }

      const api = getRanaApiV1();

      // Initialize with an empty version, in case this is a newly created publication
      let publicationVersion: PublicationVersionRead = {
        publicationId,
        id: "",
        createdAt: new Date(),
        creator: { id: "", givenName: "", familyName: "", email: "" },
        files: [],
        maps: [],
        version: 0 /* Actually saved versions will get version > 0 */,
      };

      if (version === "latest") {
        const versions = await api.PublicationVersionsApi.publicationVersionList({
          publicationId,
          limit: 1,
        });

        if (versions.items.length > 0) {
          const latestVersion = versions.items[0].version;
          publicationVersion = await api.PublicationVersionsApi.publicationVersionRetrieve({
            publicationId,
            version: latestVersion,
          });
        }
      } else {
        const versionToGet = version === "published" ? publishedVersion : version;

        if (versionToGet === null || versionToGet === undefined) {
          throw new Error("Shouldn't happen.");
        }

        publicationVersion = await api.PublicationVersionsApi.publicationVersionRetrieve({
          publicationId,
          version: versionToGet,
        });
      }

      return publicationVersion;
    },
    enabled: publicationId !== undefined,
    placeholderData: keepPreviousData,
  });
}

export const usePublicationVersionQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (
    publicationId: string | undefined,
    publishedVersion: number | undefined,
    version: "latest" | "published" | number
  ) => {
    return queryDirectly(
      queryClient,
      getPublicationVersionQuery(publicationId, publishedVersion, version)
    );
  };
};
